// ArgsView — a proposed action's arguments as a readable key/value view, never a
// raw JSON block (standing-agent-ui "Arguments readable"). Top-level fields are a
// definition list with the values in monospace (they're exact values the tool will
// receive); nested objects and arrays open into the shared collapsible JsonTree.
// Used by the proposal cards and by the inbox's approval items (which used to show
// an unformatted <pre>).

import type { CSSProperties } from 'react';
import { JsonTree } from './JsonTree';
import { parseJsonSafe } from '../lib/jsonParse';

export interface ArgsViewProps {
  /** The arguments: an object, or a JSON string of one. */
  args: unknown;
  /** Accessible name for the list (e.g. "Arguments for rmm_reboot_device"). */
  label?: string;
  /** Field names to mark as changed (an edited proposal). */
  changed?: ReadonlySet<string>;
}

const listStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(96px, max-content) minmax(0, 1fr)',
  columnGap: 12,
  rowGap: 4,
  margin: 0,
  fontSize: 13,
};

const keyStyle: CSSProperties = { color: 'var(--fg-muted)', fontWeight: 600, margin: 0 };

const valueStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  color: 'var(--fg)',
  margin: 0,
  minWidth: 0,
  overflowWrap: 'anywhere',
};

function normalize(args: unknown): unknown {
  if (typeof args === 'string') {
    return parseJsonSafe(args);
  }
  return args;
}

function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined) return 'empty';
  if (typeof value === 'string') return value.length === 0 ? '""' : value;
  return String(value);
}

export function ArgsView({ args, label, changed }: ArgsViewProps) {
  const value = normalize(args);
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    // Not an object: still readable, never a raw block.
    return value === null || value === undefined ? (
      <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>No details.</p>
    ) : (
      <JsonTree value={value} />
    );
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    return <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>No details.</p>;
  }

  return (
    <dl style={listStyle} aria-label={label ?? 'Arguments'}>
      {entries.map(([key, v]) => {
        const isChanged = changed?.has(key) ?? false;
        return (
          <div key={key} style={{ display: 'contents' }}>
            <dt style={keyStyle}>
              {key}
              {isChanged && (
                <span style={{ marginLeft: 6, color: 'var(--accent)', fontWeight: 600 }}> (changed)</span>
              )}
            </dt>
            <dd style={valueStyle}>
              {v !== null && typeof v === 'object' ? <JsonTree value={v} /> : formatPrimitive(v)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export default ArgsView;
